import styled, { css } from 'styled-components';
import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { Row, Col } from 'react-bootstrap';

import { config } from '_config';
import { theme } from '_constants/theme';
import { BLOG_SUB_ROUTES } from '_constants/routes';
import { hexToRGBA } from '_helpers/theme';
import { getBlogSubRoute, getBlogArticle } from '_helpers/blog';

import * as Layout from '_components/atomic/layouts';

import TenBestReasonsToIncorporateArticle, {
  tenBestReasonsToIncorporateContents,
} from './articles/TenBestReasonsToIncorporateArticle';

import HowToFormAnLLCStepByStepGuideArticle, {
  howToFormAnLLCStepByStepGuideContents,
} from './articles/HowToFormAnLLCStepByStepGuideArticle';

import UnderstandingTheDifferentTypesOfBusinessEntitiesArticle, {
  understandingTheDifferentTypesOfBusinessEntitiesContents,
} from './articles/UnderstandingTheDifferentTypesOfBusinessEntitiesArticle';

import CommonMistakesToAvoidWhenFormingAnLLCArticle, {
  commonMistakesToAvoidWhenFormingAnLLCContents,
} from './articles/CommonMistakesToAvoidWhenFormingAnLLCArticle';

import ChoosingTheRightNameForYourBusinessArticle, {
  choosingTheRightNameForYourBusinessContents,
} from './articles/ChoosingTheRightNameForYourBusinessArticle';

import DefiningYourBrandIdentityComprehensiveGuideArticle, {
  definingYourBrandIdentityComprehensiveGuideContents,
} from './articles/DefiningYourBrandIdentityComprehensiveGuideArticle';

import CheckingDomainAndTrademarkAvailabilityArticle, {
  checkingDomainAndTrademarkAvailabilityContents,
} from './articles/CheckingDomainAndTrademarkAvailabilityArticle';

import HowToWriteABusinessPlanStepByStepGuideArticle, {
  howToWriteABusinessPlanStepByStepGuideContents,
} from './articles/HowToWriteABusinessPlanStepByStepGuideArticle';

import RegisteringYourTrademarkGuideArticle, {
  registeringYourTrademarkGuideContents,
} from './articles/RegisteringYourTrademarkGuideArticle';

import HowToChooseBestStateToIncorporateBusinessArticle, {
  howToChooseBestStateToIncorporateBusinessContents,
} from './articles/HowToChooseBestStateToIncorporateBusinessArticle';

import BenefitsOfFormingAnLlcForSmallBusinessesArticle, {
  benefitsOfFormingAnLlcForSmallBusinessesContents,
} from './articles/BenefitsOfFormingAnLlcForSmallBusinessesArticle';

import UnderstandingDifferencesBetweenLLCAndSCorpArticle, {
  understandingDifferencesBetweenLLCAndSCorpContents,
} from './articles/UnderstandingDifferencesBetweenLLCAndSCorpArticle';

import ImportanceOfOperatingAgreementsForLLCsArticle, {
  importanceOfOperatingAgreementsForLLCsContents,
} from './articles/ImportanceOfOperatingAgreementsForLLCsArticle';

import EssentialTaxTipsForNewBusinessOwnersArticle, {
  essentialTaxTipsForNewBusinessOwnersContents,
} from './articles/EssentialTaxTipsForNewBusinessOwnersArticle';

import RoleOfRegisteredAgentsInBusinessFormationArticle, {
  roleOfRegisteredAgentsInBusinessFormationContents,
} from './articles/RoleOfRegisteredAgentsInBusinessFormationArticle';

import TopReasonsToUseProfessionalRegisteredAgentServiceArticle, {
  topReasonsToUseProfessionalRegisteredAgentServiceContents,
} from './articles/TopReasonsToUseProfessionalRegisteredAgentServiceArticle';

const ARTICLES = {
  [BLOG_SUB_ROUTES.tenBestReasonsToIncorporate]:
    TenBestReasonsToIncorporateArticle,
  [BLOG_SUB_ROUTES.howToFormAnLLCStepByStepGuide]:
    HowToFormAnLLCStepByStepGuideArticle,
  [BLOG_SUB_ROUTES.understandingTheDifferentTypesOfBusinessEntities]:
    UnderstandingTheDifferentTypesOfBusinessEntitiesArticle,
  [BLOG_SUB_ROUTES.commonMistakesToAvoidWhenFormingAnLLC]:
    CommonMistakesToAvoidWhenFormingAnLLCArticle,
  [BLOG_SUB_ROUTES.choosingTheRightNameForYourBusiness]:
    ChoosingTheRightNameForYourBusinessArticle,
  [BLOG_SUB_ROUTES.definingYourBrandIdentityComprehensiveGuide]:
    DefiningYourBrandIdentityComprehensiveGuideArticle,
  [BLOG_SUB_ROUTES.checkingDomainAndTrademarkAvailability]:
    CheckingDomainAndTrademarkAvailabilityArticle,
  [BLOG_SUB_ROUTES.howToWriteABusinessPlanStepByStepGuide]:
    HowToWriteABusinessPlanStepByStepGuideArticle,
  [BLOG_SUB_ROUTES.registeringYourTrademarkGuide]:
    RegisteringYourTrademarkGuideArticle,
  [BLOG_SUB_ROUTES.howToChooseBestStateToIncorporateBusiness]:
    HowToChooseBestStateToIncorporateBusinessArticle,
  [BLOG_SUB_ROUTES.benefitsOfFormingAnLlcForSmallBusinesses]:
    BenefitsOfFormingAnLlcForSmallBusinessesArticle,
  [BLOG_SUB_ROUTES.understandingDifferencesBetweenLLCAndSCorp]:
    UnderstandingDifferencesBetweenLLCAndSCorpArticle,
  [BLOG_SUB_ROUTES.importanceOfOperatingAgreementsForLLCs]:
    ImportanceOfOperatingAgreementsForLLCsArticle,
  [BLOG_SUB_ROUTES.essentialTaxTipsForNewBusinessOwners]:
    EssentialTaxTipsForNewBusinessOwnersArticle,
  [BLOG_SUB_ROUTES.roleOfRegisteredAgentsInBusinessFormation]:
    RoleOfRegisteredAgentsInBusinessFormationArticle,
  [BLOG_SUB_ROUTES.topReasonsToUseProfessionalRegisteredAgentService]:
    TopReasonsToUseProfessionalRegisteredAgentServiceArticle,
};

const CONTENTS = {
  [BLOG_SUB_ROUTES.tenBestReasonsToIncorporate]:
    tenBestReasonsToIncorporateContents,
  [BLOG_SUB_ROUTES.howToFormAnLLCStepByStepGuide]:
    howToFormAnLLCStepByStepGuideContents,
  [BLOG_SUB_ROUTES.understandingTheDifferentTypesOfBusinessEntities]:
    understandingTheDifferentTypesOfBusinessEntitiesContents,
  [BLOG_SUB_ROUTES.commonMistakesToAvoidWhenFormingAnLLC]:
    commonMistakesToAvoidWhenFormingAnLLCContents,
  [BLOG_SUB_ROUTES.choosingTheRightNameForYourBusiness]:
    choosingTheRightNameForYourBusinessContents,
  [BLOG_SUB_ROUTES.definingYourBrandIdentityComprehensiveGuide]:
    definingYourBrandIdentityComprehensiveGuideContents,
  [BLOG_SUB_ROUTES.checkingDomainAndTrademarkAvailability]:
    checkingDomainAndTrademarkAvailabilityContents,
  [BLOG_SUB_ROUTES.howToWriteABusinessPlanStepByStepGuide]:
    howToWriteABusinessPlanStepByStepGuideContents,
  [BLOG_SUB_ROUTES.registeringYourTrademarkGuide]:
    registeringYourTrademarkGuideContents,
  [BLOG_SUB_ROUTES.howToChooseBestStateToIncorporateBusiness]:
    howToChooseBestStateToIncorporateBusinessContents,
  [BLOG_SUB_ROUTES.benefitsOfFormingAnLlcForSmallBusinesses]:
    benefitsOfFormingAnLlcForSmallBusinessesContents,
  [BLOG_SUB_ROUTES.understandingDifferencesBetweenLLCAndSCorp]:
    understandingDifferencesBetweenLLCAndSCorpContents,
  [BLOG_SUB_ROUTES.importanceOfOperatingAgreementsForLLCs]:
    importanceOfOperatingAgreementsForLLCsContents,
  [BLOG_SUB_ROUTES.essentialTaxTipsForNewBusinessOwners]:
    essentialTaxTipsForNewBusinessOwnersContents,
  [BLOG_SUB_ROUTES.roleOfRegisteredAgentsInBusinessFormation]:
    roleOfRegisteredAgentsInBusinessFormationContents,
  [BLOG_SUB_ROUTES.topReasonsToUseProfessionalRegisteredAgentService]:
    topReasonsToUseProfessionalRegisteredAgentServiceContents,
};

const StyledOuterContainer = styled.div`
  display: flex;
  justify-content: center;

  width: 100%;
  background: ${theme.colors.white};
`;

const StyledInnerContainer = styled.div`
  max-width: ${theme.spaces.large24X * 10 + 80}px;
  width: 100%;

  padding-top: ${theme.spaces.large16X}px;
  padding-bottom: ${theme.spaces.large16X}px;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.large6X}px;
    padding-bottom: ${theme.spaces.large6X}px;
    padding-left: ${theme.spaces.regular}px;
    padding-right: ${theme.spaces.regular}px;
  }
`;

const StyledContentsContainer = styled.div`
  position: sticky;
  top: 84px;
`;

const StyledContentsBody = styled.div`
  padding: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.largeX}px;
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.indigo[600], 0.05)};
`;

const StyledContentsItem = styled.div`
  padding: ${theme.spaces.smallX}px;
  margin-bottom: ${theme.spaces.smallX}px;

  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;

  ${({ subItem }) =>
    subItem &&
    css`
      padding-left: ${theme.spaces.smallX * 3}px;
    `}

  &.active {
    border: 2px solid ${theme.colors.indigo[600]};
  }

  &:hover {
    border: 2px solid ${theme.colors.indigo[600]};
    background: ${theme.colors.indigo[600]};
    color: ${theme.colors.white};
  }
`;

const StyledAuthorContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spaces.small2X}px;
  border: 1px solid ${theme.colors.indigo[600]};
  border-radius: 50%;
  background: ${theme.colors.white};
  cursor: pointer;
`;

const StyledSocialsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.largeX}px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const BlogArticleSection = () => {
  const router = useRouter();
  const route = getBlogSubRoute(router.pathname);
  const { timeToReadMin, timestamp } = getBlogArticle(route);

  const Article = ARTICLES[route];
  const contents = CONTENTS[route];

  const handleContentItemClick = (idx) => () => {
    const anchor = document.getElementById(`${route}-${idx}`);

    if (anchor) {
      anchor.style.scrollMargin = '84px';
      anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleScroll = useCallback(() => {
    contents.forEach((_, idx) => {
      const anchor = document.getElementById(`${route}-trigger-${idx}`);

      if (anchor && anchor.classList.contains('active')) {
        anchor.classList.remove('active');
      }
    });

    const activeAnchorId = contents.reduce((activeId, _, idx) => {
      const id = `${route}-${idx}`;
      const anchor = document.getElementById(id);
      const top = anchor?.getBoundingClientRect()?.top;

      if (activeId) {
        const activeAnchor = document.getElementById(activeId);
        const activeTop = activeAnchor?.getBoundingClientRect()?.top;

        if (Math.abs(top - 30) < Math.abs(activeTop - 30)) {
          activeId = id;
        }
      } else {
        activeId = id;
      }

      return activeId;
    }, null);

    const activeTriggerId = `${route}-trigger${activeAnchorId.replace(
      route,
      ''
    )}`;

    const activeTrigger = document.getElementById(activeTriggerId);
    activeTrigger?.classList.add('active');
  }, [route, contents]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (!Article || !contents) {
    return null;
  }

  return (
    <StyledOuterContainer>
      <StyledInnerContainer>
        <Row>
          <Col xs={12} md={4}>
            <StyledContentsContainer>
              <StyledContentsBody>
                <Layout.Card.Text size={theme.fontSizes.small}>
                  Updated on: {timestamp} · {timeToReadMin} min read
                </Layout.Card.Text>
                <StyledAuthorContainer>
                  <StyledLogoContainer>
                    <picture>
                      <source
                        srcSet={`${config.basePath}/images/blog-arrow-logo.svg`}
                        type="image/svg+xml"
                      />
                      <img
                        src={`${config.basePath}/images/blog-arrow-logo.svg`}
                        alt="Author Logo"
                      />
                    </picture>
                  </StyledLogoContainer>
                  <span>
                    Written By - <b>Admin</b>
                  </span>
                </StyledAuthorContainer>
                <Layout.Card.Text
                  size={theme.fontSizes.large}
                  boldest
                  centered
                  underline
                >
                  Article Contents
                </Layout.Card.Text>
                {contents.map((item, idx) => (
                  <StyledContentsItem
                    key={item}
                    id={`${route}-trigger-${idx}`}
                    subItem={/^\s/.test(item)}
                    onClick={handleContentItemClick(idx)}
                  >
                    {item}
                  </StyledContentsItem>
                ))}
              </StyledContentsBody>
              <StyledSocialsContainer>
                <StyledIconContainer>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/blog-facebook.svg`}
                      type="image/svg+xml"
                    />
                    <img
                      src={`${config.basePath}/images/blog-facebook.svg`}
                      alt=""
                    />
                  </picture>
                </StyledIconContainer>
                <StyledIconContainer>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/blog-x.svg`}
                      type="image/svg+xml"
                    />
                    <img src={`${config.basePath}/images/blog-x.svg`} alt="" />
                  </picture>
                </StyledIconContainer>
                <StyledIconContainer>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/blog-linkedin.svg`}
                      type="image/svg+xml"
                    />
                    <img
                      src={`${config.basePath}/images/blog-linkedin.svg`}
                      alt=""
                    />
                  </picture>
                </StyledIconContainer>
              </StyledSocialsContainer>
            </StyledContentsContainer>
          </Col>
          <Col xs={12} md={8}>
            <Article idPrefix={route} />
          </Col>
        </Row>
      </StyledInnerContainer>
    </StyledOuterContainer>
  );
};

export default BlogArticleSection;
